import { Navigate, useLocation } from "react-router-dom";
import { useRef } from "react";
import { useAuth } from "../store/AuthContext";

/**
 * Guard for authenticated + role-restricted routes.
 *
 * - When not authenticated → redirect to /login (preserving the attempted URL).
 * - When authenticated but role not in `roles` → redirect to /forbidden.
 *
 * Because rendering is gated on `isAuthenticated`, browser back-button after
 * logout will not show stale protected content: the component re-runs, sees
 * a missing token, and bounces to /login.
 */
export const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, role, user } = useAuth();
  const location = useLocation();
  const lastUserId = useRef(user?.userId);
  const accountChanged = lastUserId.current != null && user?.userId != null && lastUserId.current !== user.userId;
  lastUserId.current = user?.userId;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // A different account arrived through shared localStorage. Start from its
  // role landing route rather than leaving this stale tab on a forbidden page.
  if (accountChanged) return <Navigate to="/dashboard" replace />;
  if (roles && roles.length && !roles.includes(role)) {
    return <Navigate to="/forbidden" replace />;
  }
  return children;
};
